import { Controller } from "@hotwired/stimulus"
import { useTransition, useClickOutside } from 'stimulus-use'
import Rails from "@rails/ujs"
import { template, debounce } from "lodash"
import { showNotification } from "./helpers"

export default class extends Controller {
  static targets = ["container", "input", "resourceList", "resourceTemplate", "resultTemplate", "resultTemplateCorrection", "blank", "button", "invoices",
    "menu", "outsideClose"]

  static outlets = ["invoice"]
  static values = {
    documentsKind: String,
    excluded: Array,
    income: Boolean,
    url: String,
    unoccupied: String,
    onlyOneEstimate: Boolean,
    plAdvances: String,
    mode: { type: String, default: "modal" },
    moreThanLimit: Boolean,
    refreshFailedMessage: String
  }

  eventCreated = false
  timeout = null
  lastRequestTimestamp = null
  lastValue = null
  resourceTemplate
  resultTemplate
  resultTemplateCorrection

  connect() {
    if (this.hasResourceTemplateTarget) {
      this.resourceTemplate = template(this.resourceTemplateTarget.innerHTML)
    }
    if (this.hasResultTemplateTarget) {
      this.resultTemplate = template(this.resultTemplateTarget.innerHTML)
    }
    if (this.hasResultTemplateCorrectionTarget) {
      this.resultTemplateCorrection = template(this.resultTemplateCorrectionTarget.innerHTML)
    }

    this.triggerSearch = debounce(this.triggerSearch, 500).bind(this)

    this.transitionElem = this.modeValue === "inline" ? this.menuTarget : this.containerTarget
    useTransition(this, { element: this.transitionElem })

    if (this.hasOutsideCloseTarget) {
      useClickOutside(this, {
        element: this.outsideCloseTarget
      })
    }

    // ukrywamy wyszukiwarke jesli nic nie jest zaznaczone i jest <= 10 zamowien na liscie
    // ale tylko na zaliczkach spelniajacych warunek advance_with_only_one_estimate?
    if (this.onlyOneEstimateValue && this.hasButtonTarget) {
      const checkedCheckboxes = this.invoicesTargets.filter(el =>
        el.querySelector("input[type='checkbox']")?.checked
      )

      if (checkedCheckboxes.length === 0 && !this.moreThanLimitValue) {
        this.toogleOtherEstimates(true, null, false)
      }
    }

    // odśwież preloadowanych kandydatów po zmianie nabywcy (event z dropdown_client_controller);
    // działa w obu trybach (inline desktop / modal mobile) — lista kandydatów jest na stronie
    this.boundClientChanged = this.refreshCandidates.bind(this)
    document.addEventListener("invoice:client-changed", this.boundClientChanged)
  }

  open() {
    this.toggle()
    this.search()
  }

  triggerSearch(event) {
    this.resourceListTarget.innerHTML = ""
    if (this.modeValue === "inline" && event.target.value.length < 2) {
      this.hide()
      return
    }

    if (this.lastValue === this.inputTarget.value) {
      return
    }

    this.lastValue = this.inputTarget.value
    this.search()
  }

  // Po wejściu w pole (focus/klik) — gdy wybrany jest klient, od razu pokazujemy jego
  // zaliczki/zamówienia (serwer zwraca dokumenty klienta na górze) bez konieczności wpisywania frazy.
  focusSearch() {
    if (this.modeValue === "inline" && this.inputTarget.value.length < 2 && !this.clientId()) {
      return
    }

    this.lastValue = this.inputTarget.value
    this.search()
  }

  clientId() {
    return document.getElementById("invoice_client_id")?.value
  }

  // Po zmianie nabywcy odświeżamy preloadowanych kandydatów: zaznaczone (ręcznie dodane /
  // podpięte) zostają, niezaznaczonych kandydatów podmieniamy na dokumenty nowego klienta.
  // Starych kandydatów usuwamy dopiero po pobraniu nowych — jeden reflow zamiast dwóch skoków.
  refreshCandidates() {
    // bez wyszukiwarki (with_dropdown=false) nie ma szablonu wiersza — dynamiczny refresh nieaktywny
    if (!this.hasResultTemplateTarget) return
    // instancja bez podłączonego outletu invoice (np. inny dropdown-invoices na stronie) — nic tu nie odświeżamy
    if (!this.hasInvoiceOutlet) return

    const list = this.element.querySelector("[data-target~='invoicesList']")
    if (!list) return

    const keptIds = this.invoicesTargets
      .map(row => parseInt(row.querySelector("input[type='checkbox']:checked")?.value))
      .filter(id => !isNaN(id))

    const clientId = this.clientId()
    if (!clientId) {
      this.removeUncheckedCandidates(list)
      this.excludedValue = keptIds
      this.toggleEmptyList()
      return
    }

    const params = new URLSearchParams({
      kind: this.documentsKindValue,
      name: "",
      income: this.invoiceOutlet.incomeValue,
      include_kind: 1,
      display_only_unoccupied: this.unoccupiedValue,
      with_corrections: this.plAdvancesValue,
      client_id: clientId
    })
    keptIds.forEach(id => params.append("exclude_ids[]", id))

    Rails.ajax({
      url: this.urlValue,
      type: "get",
      data: params,
      success: (data) => {
        this.removeUncheckedCandidates(list) // dopiero teraz — po pobraniu nowych
        this.appendCandidates(data, keptIds)
      },
      error: (err) => {
        console.error("Błąd odświeżania powiązanych dokumentów", err)
        // błąd serwera — bezpieczniej wyczyścić kandydatów poprzedniego klienta niż zostawić ich w formularzu
        this.removeUncheckedCandidates(list)
        this.excludedValue = keptIds
        this.toggleEmptyList()
        showNotification(this.refreshFailedMessageValue)
      }
    })
  }

  removeUncheckedCandidates(list) {
    this.invoicesTargets.forEach(row => {
      const cb = row.querySelector("input[type='checkbox']")
      const id = parseInt(cb?.value)
      if (isNaN(id) || cb?.checked) return
      list.querySelectorAll(`[from-invoice-id='${id}']`).forEach(el => el.remove())
      row.remove()
    })
  }

  appendCandidates(data, keptIds) {
    const list = this.element.querySelector("[data-target~='invoicesList']")
    if (!list) return

    const candidateIds = []
    data.forEach(invoice => {
      if (keptIds.includes(invoice.id)) return

      list.insertAdjacentHTML("beforeend", this.resultTemplate({ id: invoice.id, label: invoice.display_label }))
      const checkbox = list.querySelector(`#invoice_invoice_ids_${invoice.id}`)
      if (checkbox) checkbox.checked = false // kandydat — niezaznaczony (podpowiedź)
      candidateIds.push(invoice.id)

      if (this.resultTemplateCorrection && "corrections" in invoice) {
        invoice.corrections.forEach(corr => {
          list.insertAdjacentHTML("beforeend", this.resultTemplateCorrection({
            id: invoice.id, number: corr.number, price_with_currency: corr.price_with_currency, issue_date: corr.issue_date
          }))
        })
      }
    })

    this.excludedValue = [...keptIds, ...candidateIds]
    this.toggleEmptyList()
  }

  toggleEmptyList() {
    const hasRows = this.invoicesTargets.length > 0
    this.element.querySelector("[data-target~='emptyList']")?.classList.toggle("hidden", hasRows)
  }

  search() {
    if (!this.hasInvoiceOutlet) return

    this.blankTarget.classList.add("hidden")
    this.resourceListTarget.classList.remove("hidden")

    const params = new URLSearchParams({
      rows: 10,
      kind: this.documentsKindValue,
      name: this.inputTarget.value,
      income: this.invoiceOutlet.incomeValue,
      include_kind: 1,
      display_only_unoccupied: this.unoccupiedValue,
      with_corrections: this.plAdvancesValue
    })
    const clientId = this.clientId()
    if (clientId) {
      params.append("client_id", clientId)
    }
    this.excludedValue.forEach(id => {
      params.append("exclude_ids[]", id)
    })

    Rails.ajax({
      url: this.urlValue,
      type: "get",
      data: params,
      success: (data) => this.showResults(data),
      error: (err) => console.error("Błąd w autocomplete", err)
    })
  }

  showResults(data) {
    const hasResults = data.length > 0

    this.resourceListTarget.innerHTML = ""
    if (this.modeValue === "inline") {
      this.renderItems(data)
      this.menuTarget.classList.toggle("hidden", false)
    } else {
      this.renderItems(data)
      this.resourceListTarget.classList.toggle("hidden", !hasResults)
    }

    this.blankTarget.classList.toggle("hidden", hasResults)
  }

  renderItems(data) {
    data.forEach((invoice) => {
      this.itemData = {
        id: invoice.id,
        label: invoice.label,
        display_label: invoice.display_label,
        number: invoice.number,
        buyer_name: invoice.buyer_name,
        issue_date: this.documentsKindValue === "advance" ? invoice.order_date : invoice.issue_date,
        price: invoice.price_with_currency,
        corrections: "corrections" in invoice ? JSON.stringify(invoice.corrections).replaceAll("\"", "'") : "[]"
      }
      this.resourceListTarget.innerHTML += this.resourceTemplate(this.itemData)
    })
  }

  select(event) {
    event.preventDefault()
    if (this.modeValue === "inline") {
      this.menuTarget.classList.toggle("hidden", true)
    }

    const invoiceId = event.params.id

    // Sprawdź czy element już istnieje w liście (może być ukryty po poprzednim zaznaczeniu)
    const existingTarget = this.invoicesTargets.find(el => {
      return parseInt(el.querySelector("input[type='checkbox']")?.value) === invoiceId
    })

    if (existingTarget) {
      // Element już jest w DOM — odkryj go i zaznacz checkbox zamiast dodawać duplikat
      existingTarget.classList.remove("hidden")
      const checkbox = existingTarget.querySelector("input[type='checkbox']")
      if (checkbox) checkbox.checked = true
    } else {
      document.querySelector("[data-target~='emptyList']")?.classList.add("hidden")
      document.querySelector("[data-target~='invoicesList']")
        .insertAdjacentHTML("beforeend", this.resultTemplate({ id: invoiceId, label: event.params.label }))
      if (typeof event.params.corrections === 'string' || event.params.corrections instanceof String) {
        JSON.parse(event.params.corrections.replaceAll("'", "\""))
          .forEach((element) => {
            document.querySelector("[data-target~='invoicesList']")
              .insertAdjacentHTML("beforeend", this.resultTemplateCorrection({ id: invoiceId, number: element.number, price_with_currency: element.price_with_currency, issue_date: element.issue_date }))
          }, this)
      }
    }

    if (this.onlyOneEstimateValue) {
      this.excludedValue = [invoiceId]
      this.toogleOtherEstimates(true, invoiceId)
    } else {
      this.excludedValue = [...this.excludedValue, invoiceId]
    }
    this.toggle()
  }

  checkEstimates(event) {
    if (event.target.checked) {
      this.toogleOtherEstimates(true, parseInt(event.target.value))
    } else {
      this.toogleOtherEstimates(false)
    }
  }

  toogleOtherEstimates(hide = true, selectedElemId = null, removeElements = true) {
    if (this.modeValue === "inline") {
      this.menuTarget.classList.toggle("hidden", true)
    }
    if (hide) {
      if (selectedElemId && removeElements) {
        this.invoicesTargets.forEach((element) => {
          const checkboxVal = parseInt(element.querySelector("input[type='checkbox']")?.value)
          if (checkboxVal !== selectedElemId) {
            element.classList.add("hidden")
            document.querySelectorAll(`[from-invoice-id='${checkboxVal}']`).forEach(el => el.classList.add("hidden"))
          }
        })
        this.excludedValue = [selectedElemId]
      }
      this.buttonTarget.classList.add("hidden")
    } else {
      // Przywróć pierwotną listę zamówień zamiast otwierać wyszukiwarkę
      this.invoicesTargets.forEach((element) => {
        element.classList.remove("hidden")
        const checkboxVal = parseInt(element.querySelector("input[type='checkbox']")?.value)
        document.querySelectorAll(`[from-invoice-id='${checkboxVal}']`).forEach(el => el.classList.remove("hidden"))
      })
      // Wyklucz z wyszukiwarki wszystkie elementy widoczne na liście (można je kliknąć bezpośrednio)
      this.excludedValue = this.invoicesTargets.map(el =>
        parseInt(el.querySelector("input[type='checkbox']")?.value)
      ).filter(Boolean)
      if (this.moreThanLimitValue) {
        this.buttonTarget.classList.remove("hidden")
      }
    }
  }

  toggle() {
    this.toggleTransition()
    if (this.eventCreated === false) {
      if (this.hasOutsideCloseTarget) {
        const fn = this.clickOutside.bind(this)
        this.eventCreated = fn
        this.inputTarget.value = ""
        this.inputTarget.focus()
        document.addEventListener("click", fn)
      }
    } else {
      this.removeEvent()
    }
  }

  hide() {
    // console.log('hide1', this.element, event.target)
    this.leave()
    this.removeEvent()
    if (this.hasMenuTarget) this.menuTarget.classList.add("hidden")
    this.resourceListTarget.innerHTML = ""
  }

  clickOutside() {
    if (this.hasMenuTarget && !this.menuTarget.classList.contains("hidden")) {
      this.hide()
    }
  }

  removeEvent() {
    if (this.eventCreated !== false) {
      document.removeEventListener("click", this.eventCreated)
      this.eventCreated = false
    }
  }

  disconnect() {
    this.removeEvent()
    if (this.boundClientChanged) {
      document.removeEventListener("invoice:client-changed", this.boundClientChanged)
    }
  }
}
